import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInquiry } from '../contexts/InquiryContext';
import { sendInquiry } from '../api/inquiry';
import SEOHead from '../components/seo/SEOHead';

const InquiryPage: React.FC = () => {
    const { state, removeItem, clearInquiry } = useInquiry();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        captchaAnswer: ''
    });
    const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, sum: 0 });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        const n1 = Math.floor(Math.random() * 10) + 1;
        const n2 = Math.floor(Math.random() * 10) + 1;
        setCaptcha({ num1: n1, num2: n2, sum: n1 + n2 });
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        if (parseInt(formData.captchaAnswer) !== captcha.sum) {
            setError('Antwoord onjuist.');
            return;
        }

        setLoading(true);
        try {
            await sendInquiry({ ...formData, items: state.items, num1: captcha.num1, num2: captcha.num2 });
            setSubmitted(true);
            clearInquiry();
        } catch (err) {
            setError('Er is een fout opgetreden bij het verzenden. Controleer uw gegevens.');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <main className="pt-40 pb-20 container mx-auto px-6 text-center">
                <SEOHead
                    title="Aanvraag Verzonden"
                    description="Bedankt voor uw aanvraag bij Kunstlounge."
                    canonical="/aanvraag/"
                />
                <h1 className="text-4xl font-serif mb-6">Bedankt voor uw aanvraag</h1>
                <p className="text-gray-600">Wij hebben uw bericht ontvangen en nemen zo spoedig mogelijk contact op.</p>
            </main>
        );
    }

    return (
        <main className="pt-40 pb-20 container mx-auto px-6">
            <SEOHead
                title="Aanvraag Bezichtiging"
                description="Heeft u interesse in een specifiek kunstwerk? Vraag een privébezichtiging aan voor originele Clemens Briels meesterwerken in onze beveiligde exclusieve lounge."
                canonical="/aanvraag/"
                hreflangEn="/en/contact.html"
                hreflangNl="/aanvraag/"
                schemaData={[{
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "name": "Aanvraag Bezichtiging bij Kunstlounge",
                    "description": "Privébezichtiging aanvragen voor originele Clemens Briels kunstwerken uit de privécollectie van Kunstlounge.",
                    "url": "https://www.kunstlounge.nl/aanvraag",
                    "inLanguage": "nl",
                    "isPartOf": {
                        "@type": "WebSite",
                        "name": "Kunstlounge",
                        "url": "https://www.kunstlounge.nl"
                    }
                },
                {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    "mainEntity": [
                        {
                            "@type": "Question",
                            "name": "Wat kost een schilderij van Clemens Briels?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "De prijzen variëren per werk. Gelimiteerde zeefdrukken starten vanaf circa €1.100. Unieke originele schilderijen op doek zijn geprijsd vanaf €5.500. De exacte prijs wordt op aanvraag verstrekt via het contactformulier."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "Kan ik een kunstwerk bezichtigen voordat ik het koop?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Ja, bezichtiging is mogelijk op afspraak in de privélounge (regio Eindhoven/Noord-Brabant). Neem contact op via het aanvraagformulier om een afspraak te maken."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "Hoe vraag ik een kunstwerk aan?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Voeg het gewenste kunstwerk toe aan uw aanvraaglijst en vul het contactformulier in op deze pagina. Wij nemen persoonlijk contact met u op."
                            }
                        },
                        {
                            "@type": "Question",
                            "name": "Wordt een kunstwerk verzekerd geleverd?",
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": "Ja, alle werken worden verzekerd verzonden. Levering geschiedt zorgvuldig verpakt met professionele kunstverpakking."
                            }
                        }
                    ]
                }]}
            />
            <h1 className="text-4xl font-serif mb-12">Uw aanvraaglijst</h1>
            {state.items.length === 0 ? (
                <div className="text-center py-20 max-w-xl mx-auto">
                    <div className="text-accent text-4xl mb-8">✦</div>
                    <h2 className="text-2xl font-serif text-primary mb-6">Uw aanvraaglijst is leeg</h2>
                    <p className="text-gray-500 font-light leading-relaxed mb-10">
                        U heeft nog geen kunstwerken geselecteerd. Blader door onze exclusieve collectie, kies één of meerdere werken die u aanspreken en voeg ze toe aan uw aanvraaglijst.
                    </p>
                    <Link
                        to="/#collectie"
                        className="inline-block px-12 py-5 bg-primary text-white hover:bg-accent transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold shadow-xl"
                    >
                        Ga naar collectie &amp; kies een kunstwerk
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
                    <div>
                        <h2 className="text-2xl font-serif mb-6 border-b pb-2">Geselecteerde werken</h2>
                        {state.items.map(item => (
                            <div key={item.id} className="flex gap-6 mb-6 border-b pb-6 group">
                                <img src={item.imageUrl} alt={item.title} className="w-32 h-32 object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                <div className="flex-1">
                                    <h3 className="text-xl font-serif mb-2">{item.title}</h3>
                                    <button
                                        onClick={() => removeItem(item.id)}
                                        className="text-xs uppercase tracking-widest text-red-400 hover:text-red-600 transition-colors"
                                    >
                                        Verwijder uit lijst
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-gray-50 p-10 border border-gray-100">
                        <h2 className="text-2xl font-serif mb-8 text-center">Contactgegevens</h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <input type="text" name="name" placeholder="Naam" onChange={handleChange} required className="w-full p-4 bg-white border border-gray-200 focus:border-accent outline-none transition-colors" />
                            <input type="email" name="email" placeholder="E-mailadres" onChange={handleChange} required className="w-full p-4 bg-white border border-gray-200 focus:border-accent outline-none transition-colors" />
                            <input type="tel" name="phone" placeholder="Telefoonnummer (optioneel)" onChange={handleChange} className="w-full p-4 bg-white border border-gray-200 focus:border-accent outline-none transition-colors" />
                            <textarea name="message" placeholder="Uw bericht of specifieke vragen" rows={4} onChange={handleChange} className="w-full p-4 bg-white border border-gray-200 focus:border-accent outline-none transition-colors" />

                            <div className="bg-white p-6 border border-gray-200 text-center">
                                <p className="text-sm text-gray-400 mb-4 tracking-widest uppercase">Spambescherming</p>
                                <div className="flex items-center justify-center gap-4 text-xl font-serif">
                                    <span>{captcha.num1} + {captcha.num2} =</span>
                                    <input
                                        type="number"
                                        name="captchaAnswer"
                                        onChange={handleChange}
                                        required
                                        className="w-20 p-2 border-b border-gray-300 focus:border-accent text-center outline-none"
                                    />
                                </div>
                            </div>

                            {error && <p className="text-red-500 text-sm text-center italic">{error}</p>}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-black text-white py-5 px-8 hover:bg-accent transition-all duration-700 tracking-[0.3em] uppercase text-sm disabled:bg-gray-400"
                            >
                                {loading ? 'Verzenden...' : 'Informatie aanvragen'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* FAQ */}
            <div className="mt-24 pt-20 border-t border-gray-100 max-w-3xl mx-auto">
                <h2 className="text-3xl font-serif text-primary mb-12">Veelgestelde vragen over prijs, bezichtiging en levering</h2>
                <div className="space-y-8">
                    {[
                        {
                            q: 'Wat kost een schilderij van Clemens Briels?',
                            a: 'De prijzen variëren per werk. Gelimiteerde zeefdrukken starten vanaf circa €1.100. Unieke originele schilderijen op doek zijn geprijsd vanaf €5.500. De exacte prijs wordt op aanvraag verstrekt via het contactformulier.'
                        },
                        {
                            q: 'Kan ik een kunstwerk bezichtigen voordat ik het koop?',
                            a: 'Ja, bezichtiging is mogelijk op afspraak in de privélounge (regio Eindhoven/Noord-Brabant). Neem contact op via het aanvraagformulier om een afspraak te maken.'
                        },
                        {
                            q: 'Hoe vraag ik een kunstwerk aan?',
                            a: 'Voeg het gewenste kunstwerk toe aan uw aanvraaglijst en vul het contactformulier in op deze pagina. Wij nemen persoonlijk contact met u op.'
                        },
                        {
                            q: 'Wordt een kunstwerk verzekerd geleverd?',
                            a: 'Ja, alle werken worden verzekerd verzonden. Levering geschiedt zorgvuldig verpakt met professionele kunstverpakking.'
                        },
                    ].map(({ q, a }) => (
                        <div key={q} className="border-b border-gray-100 pb-8">
                            <h3 className="font-serif text-primary text-xl mb-3">{q}</h3>
                            <p className="text-gray-500 font-light leading-relaxed">{a}</p>
                        </div>
                    ))}
                </div>
            </div>
        </main>
    );
};

export default InquiryPage;
